import BaseRepository from "~/api/BaseRepository";
import type { $Fetch } from "nitropack";

export interface MissionFeedback {
  mission_feedback_id: string;
  mission_id: string;
  public_comment: string | null;
  private_comment: string | null;
  score: number;
  contact_email: string | null;
  feedback_type: 'manager' | 'client' | 'freelance';
  created_at: string;
  token: string;
  status: 'open' | 'accepted' | 'rejected';
  reply_text: string | null;
  replied_at: string | null;
}

export interface MissionFeedbackPublicView {
  mission_feedback_id: string;
  mission_id: string;
  public_comment: string | null;
  score: number;
  feedback_type: 'manager' | 'client' | 'freelance';
  created_at: string;
  status: 'open' | 'accepted' | 'rejected';
  reply_text: string | null;
  replied_at: string | null;
}

export interface CompanyFeedbackExchangeResponse {
  freelance_feedback: MissionFeedbackPublicView | null;
  company_feedbacks: MissionFeedbackPublicView[];
}

export interface Company {
  company_id: string;
  public_name: string;
  name?: string;
  siren?: string;
  // Add other company fields as needed
}

export type ExperienceType = 'proven' | 'declarative';

export interface CreatorInfo {
  user_id: string;
  first_name: string;
  last_name: string;
  picture_url: string | null;
}

export interface ExperienceWithFeedbackResponse {
  // All experience/mission fields (flattened from backend)
  id: string;
  mission_id?: string;
  object: string;
  created_at: string;
  updated_at: string;
  company_siren: string | null;
  company_name: string | null;
  company: Company;
  budget: number | null;
  commission: number | null;
  description: string;
  title: string;
  is_public: boolean;
  status: string;
  mobility_type: string | null;
  start_at?: string;
  end_at?: string;
  pipeline_revenue?: string | null;
  contract_type?: string | null;
  industry?: {
    industry_id: string;
    name: string;
    category?: any;
  } | null;
  company_size?: {
    company_size_id: string;
    name: string;
    min_people: number;
    max_people: number;
  } | null;
  market_segments?: Array<{
    market_segment_id: string;
    name: string;
    min_employees: number;
    max_employees: number;
  }>;
  personas?: Array<{
    persona_id: string;
    name: string;
    category?: any;
  }>;
  deal_sizes?: Array<{
    deal_size_id: string;
    name: string;
    min_amount: string;
    max_amount: string;
  }>;
  skills?: Array<{
    skill_id: string;
    name: string;
    description?: string;
    category?: any;
  }>;
  // Whether this mission has been converted to an experience
  is_experience?: boolean;
  // Feedbacks array (every feedback on the experience)
  feedbacks: MissionFeedback[];
  // The feedback the opened token points to (this recipient's own row).
  // Pre-fill the form from THIS one only — never from `feedbacks[0]`, which would leak
  // another recipient's comment.
  current_feedback: MissionFeedbackPublicView;
  // Experience type: 'proven' (with invitation) or 'declarative' (manual)
  experience_type: ExperienceType;
  // Creator info for declarative experiences (use created_by instead of invitation)
  creator?: CreatorInfo;
  // Mission status and completion info (for validation vs feedback mode)
  mission_status: string;
  completion_requested_at: string | null;
}

export interface ValidateCompletionResponse {
  accepted: boolean;
  feedback_token?: string;
}

export const MissionFeedbackRepository = (client: $Fetch) => ({
  ...BaseRepository(client)('v2/mission-feedback'),

  fetchByToken: (token: string) => {
    return client(`/v2/mission-feedback/${token}`, {
      method: 'GET',
      headers: {
        "accept": "application/json"
      }
    }) as Promise<ExperienceWithFeedbackResponse>;
  },

  validateCompletion: (token: string, payload: { accepted: boolean }) => {
    return client(`/v2/mission-completion/${token}/validate`, {
      method: 'POST',
      body: payload,
      headers: {
        "accept": "application/json",
        "Content-Type": "application/json"
      }
    }) as Promise<ValidateCompletionResponse>;
  },

  requestCompletion: (missionId: string) => {
    return client(`/v2/mission-completion/request/${missionId}`, {
      method: 'POST',
      headers: {
        "accept": "application/json"
      }
    }) as Promise<{ success: boolean }>;
  },

  getCompanyFeedback: (experienceId: string) => {
    return client(`/v2/experiences/${experienceId}/company-feedback`, {
      method: 'GET',
      headers: { "accept": "application/json" }
    }) as Promise<CompanyFeedbackExchangeResponse>;
  },

  submitCompanyFeedback: (experienceId: string, payload: { score: number; public_comment?: string | null }) => {
    return client(`/v2/experiences/${experienceId}/company-feedback`, {
      method: 'PUT',
      body: payload,
      headers: { "accept": "application/json", "Content-Type": "application/json" }
    }) as Promise<MissionFeedbackPublicView>;
  },

  replyToFeedback: (feedbackId: string, payload: { reply_text: string }) => {
    return client(`/v2/mission-feedback/${feedbackId}/reply`, {
      method: 'PUT',
      body: payload,
      headers: { "accept": "application/json", "Content-Type": "application/json" }
    }) as Promise<MissionFeedbackPublicView>;
  },
});
